import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth } from 'date-fns';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TrendingDown, TrendingUp, Wallet, ListChecks } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MillionHero } from '@/components/MillionHero';
import { api } from '@/lib/api';
import { formatChf } from '@/lib/format';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

function DashboardPage() {
  const today = new Date();
  const from = format(startOfMonth(today), 'yyyy-MM-dd');
  const to = format(today, 'yyyy-MM-dd');

  const { data, isLoading } = useQuery({
    queryKey: ['kpis', from, to],
    queryFn: () => api.dashboardKpis(from, to),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Vue d'ensemble du mois courant</p>
      </div>

      <MillionHero />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          title="Revenus"
          value={data?.kpis.income ?? 0}
          tone="success"
          icon={<TrendingUp className="h-4 w-4" />}
          loading={isLoading}
        />
        <Kpi
          title="Dépenses"
          value={data?.kpis.expense ?? 0}
          tone="destructive"
          icon={<TrendingDown className="h-4 w-4" />}
          loading={isLoading}
        />
        <Kpi
          title="Solde net"
          value={data?.kpis.net ?? 0}
          tone={data && data.kpis.net >= 0 ? 'success' : 'destructive'}
          icon={<Wallet className="h-4 w-4" />}
          loading={isLoading}
        />
        <Kpi
          title="Transactions"
          value={data?.kpis.txCount ?? 0}
          tone="muted"
          icon={<ListChecks className="h-4 w-4" />}
          isCount
          loading={isLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Évolution journalière</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={(data?.daily ?? []).map((d) => ({ day: d.day.slice(5), net: d.net }))}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <RTooltip
                  formatter={(v: number) => formatChf(v)}
                  contentStyle={{
                    background: 'hsl(var(--card-bg))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                  }}
                />
                <Area type="monotone" dataKey="net" stroke="hsl(var(--primary))" fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top contreparties</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(data?.topMerchants ?? []).slice(0, 8).map((m) => (
                <li key={m.counterparty} className="flex items-center justify-between text-sm">
                  <span className="truncate">{m.counterparty}</span>
                  <span className="num-tnum text-destructive">- {formatChf(m.total)}</span>
                </li>
              ))}
              {(!data || data.topMerchants.length === 0) && !isLoading ? (
                <li className="text-sm text-muted-foreground">Aucune dépense ce mois</li>
              ) : null}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Par catégorie (dépenses)</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(data?.byCategory ?? [])
                .filter((c) => c.total < 0)
                .sort((a, b) => a.total - b.total)
                .slice(0, 8)
                .map((c) => (
                  <li key={c.categoryId ?? 'none'} className="flex items-center justify-between text-sm">
                    <span className="truncate">{c.categoryName ?? 'Non catégorisé'}</span>
                    <span className="num-tnum text-destructive">{formatChf(c.total)}</span>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({
  title,
  value,
  tone,
  icon,
  isCount,
  loading,
}: {
  title: string;
  value: number;
  tone: 'success' | 'destructive' | 'muted';
  icon: React.ReactNode;
  isCount?: boolean;
  loading?: boolean;
}) {
  const colorMap = {
    success: 'text-success',
    destructive: 'text-destructive',
    muted: 'text-foreground',
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm text-muted-foreground">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold num-tnum ${colorMap[tone]}`}>
          {loading ? '…' : isCount ? value.toLocaleString('fr-CH') : formatChf(value)}
        </div>
      </CardContent>
    </Card>
  );
}
